import { useEffect, useState } from 'react'
import { Col, Empty, Row } from 'antd'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'

import type { WishlistItemWithActions, WishlistListProps } from './types'
import WishlistItem, { WishlistItemSkeleton } from './WishlistItem'
import SortableWishlistItem from './SortableWishlistItem'
import { useRelocateWishlists, WISHLIST_OVERVIEW_PAGE_SIZE } from './useFetchWishlist'
import styles from './WishlistItem.module.scss'

const emptyWishlist: WishlistItemWithActions[] = []

const WishlistList = ({
  items = emptyWishlist,
  isLoading = false,
  disableDragAndDrop = false,
  pageNumber = 1,
  pageSize = WISHLIST_OVERVIEW_PAGE_SIZE,
}: WishlistListProps) => {
  const [localItems, setLocalItems] = useState<WishlistItemWithActions[]>(items)
  const [activeItem, setActiveItem] = useState<WishlistItemWithActions | null>(null)

  const { mutate: relocateWishlists } = useRelocateWishlists()

  useEffect(() => {
    setLocalItems(items)
  }, [items])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragStart = (event: DragStartEvent) => {
    const item = localItems.find((i) => i.wishlistId === event.active.id)
    setActiveItem(item ?? null)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveItem(null)

    if (!over || active.id === over.id) return

    const oldIndex = localItems.findIndex((i) => i.wishlistId === active.id)
    const newIndex = localItems.findIndex((i) => i.wishlistId === over.id)

    if (oldIndex < 0 || newIndex < 0) return

    const reordered = arrayMove(localItems, oldIndex, newIndex)

    setLocalItems(reordered)

    relocateWishlists(
      reordered.map((item, index) => ({
        ownerId: item.ownerId,
        wishlistId: item.wishlistId,
        order: (pageNumber - 1) * pageSize + index + 1,
      })),
      { onError: () => setLocalItems(localItems) },
    )
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveItem(null)}
    >
      {isLoading
        ? (
          <Row gutter={[24, 24]} justify="start">
            {[0, 1, 2, 3, 4, 5].map((item) => (
              <Col key={item} md={12} span={24} xl={8}>
                <WishlistItemSkeleton />
              </Col>
            ))}
          </Row>
        )
        : localItems.length === 0
          ? (
            <Row justify="center" align="middle">
              <Col>
                <Empty />
              </Col>
            </Row>
          )
          : (
            <div className={activeItem ? styles.dragActiveContainer : undefined}>
              <Row gutter={[24, 24]}>
                <SortableContext
                  items={localItems.map((item) => item.wishlistId)}
                  strategy={rectSortingStrategy}
                >
                  {localItems.map((item) => (
                    <SortableWishlistItem key={item.wishlistId} {...item} disableDragAndDrop={disableDragAndDrop} />
                  ))}
                </SortableContext>
              </Row>
            </div>
          )
      }

      <DragOverlay>
        {activeItem && (
          <div style={{ opacity: 0.9, cursor: 'grabbing' }}>
            <WishlistItem {...activeItem} isDragging dragHandleProps={{}} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}

export default WishlistList
